import SEOHead from "@/components/SEOHead";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLocale } from "@/i18n/LocaleProvider";

const Imprint = () => {
  const ref = useScrollReveal();
  const { t } = useTranslation();
  const { localize } = useLocale();

  return (
    <div className="min-h-screen">
      <SEOHead
        title={t("imprint.metaTitle")}
        description={t("imprint.metaDescription")}
        canonical="https://devcore.systems/imprint"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: t("nav.home"), item: "https://devcore.systems/" },
            { "@type": "ListItem", position: 2, name: t("imprint.breadcrumb"), item: "https://devcore.systems/imprint" },
          ],
        }}
      />

      <Navbar />
      <main className="pt-28 pb-24 md:pt-36 md:pb-32">
        <div ref={ref} className="container section-reveal max-w-3xl">
          <Link
            to={localize("/")}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            {t("imprint.back")}
          </Link>

          <h1 className="text-[1.55rem] sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-12">
            {t("imprint.title")}
          </h1>

          <div className="space-y-10">
            <section>
              <h2 className="text-xl font-bold text-foreground mb-3">{t("imprint.responsible")}</h2>
              <div className="text-muted-foreground leading-relaxed space-y-1">
                <p className="font-medium text-foreground">DevCore Websystems Ltd.</p>
                <p>Tremithias, 1, Salacia Beach Residence Flat/Office 41</p>
                <p>8310 Paphos, Cyprus</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mb-3">{t("imprint.contact")}</h2>
              <div className="text-muted-foreground leading-relaxed space-y-1">
                <p>
                  {t("imprint.emailLabel")}:{" "}
                  <a href="mailto:mail@devcore.systems" className="text-primary hover:underline">
                    mail@devcore.systems
                  </a>
                </p>
                <p>
                  {t("imprint.phoneLabel")}:{" "}
                  <a href="tel:+35796333124" className="text-primary hover:underline">
                    +357 96 333 124
                  </a>
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mb-3">{t("imprint.registration")}</h2>
              <p className="text-muted-foreground">{t("imprint.taxId")}: 10430643F</p>
              <p className="text-muted-foreground">{t("imprint.registerNo")}: ΗΕ 430643</p>
              <a
                href="https://efiling.drcor.mcit.gov.cy/DrcorPublic/SearchResults.aspx?name=DEVCORE+WEBSYSTEMS+LTD&number=430643&searchtype=optStartMatch&index=1&tname=%25&sc="
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-2"
              >
                {t("imprint.registerLink")} →
              </a>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mb-3">{t("imprint.supervisory")}</h2>
              <p className="text-muted-foreground">{t("imprint.supervisoryValue")}</p>
            </section>

            <section className="pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} DevCore Websystems Ltd. {t("imprint.rights")}
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Imprint;
